/**
 * Enhanced HomeworkCreate using the new component system
 * Demonstrates form layout with complex validation and file handling
 */

import isEmpty from 'lodash/isEmpty';
import { parse } from 'node-html-parser';
import React, { useState } from 'react';
import { FiBook, FiUpload } from 'react-icons/fi';
import { RiBold, RiPinyinInput } from 'react-icons/ri';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';

// New component system imports
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Card, FormField, FormPageLayout, Input } from '@grcs/components';

// Rich text editors
import { ArticleEditor } from '../shared/components/editors/ArticleEditor';
import { QuestionEditor } from '../shared/components/editors/QuestionEditor';

// Existing hooks and utilities
import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { ArticleProfile } from '@grcs/types/DataTypes';
import { UserRole } from '@grcs/types/UserRole';
import { countArticleCharacters } from '@grcs/utils/content/countArticleCharacters';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { uploadFile } from '@grcs/utils/storage/databaseStorageLoader';
import { parseAndBold } from '@grcs/utils/formatting/parseAndBold';
import { parseAndDisplayPinyin } from '@grcs/utils/formatting/parseAndDisplayPinyin';
import { ALL_CHAPTER, ALL_GRADES, FILE_DB_BUCKET_NAME } from '../const';

interface HomeworkFormData {
  title: string;
  grade: string;
  chapter: string;
  article: string;
  article_pinyin: string;
  question: string;
  question_pinyin: string;
  file: File | null;
}

export const HomeworkCreateEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const userOps = useUserOperations();
  const toast = useToast();
  const [{ full_name }] = useLocalStorage('user', {});
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const selectedFileBg = useColorModeValue('blue.50', 'blue.900');
  const selectedFileBorder = useColorModeValue('blue.200', 'blue.700');

  const [formData, setFormData] = useState<HomeworkFormData>({
    title: '',
    grade: '',
    chapter: '',
    article: '',
    article_pinyin: '',
    question: '[]', // Initialize as empty JSON array for questions
    question_pinyin: '[]',
    file: null,
  });

  const [errors, setErrors] = useState<Partial<HomeworkFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [articleTabIndex, setArticleTabIndex] = useState(0);
  const [questionTabIndex, setQuestionTabIndex] = useState(0);

  const handleInputChange = (
    field: keyof HomeworkFormData,
    value: string | File,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleInputChange('file', file);
    }
  };

  // Pinyin and formatting functions
  const generatePinyin = (text: string) => {
    const tmp = parse(text || '');
    const tmp_pinyin = parseAndDisplayPinyin({
      article: tmp.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });
    return tmp_pinyin;
  };

  const handleGeneratePinyin = () => {
    if (!validateInputsForPinyin()) return;
    setFormData((prev) => ({
      ...prev,
      article_pinyin: generatePinyin(prev.article),
    }));
    setArticleTabIndex(1);
  };

  const handleGeneratePinyinForQuestion = () => {
    if (!validateInputsForPinyin()) return;
    setFormData((prev) => ({
      ...prev,
      question_pinyin: generatePinyin(prev.question),
    }));
    setQuestionTabIndex(1);
  };

  const handleBoldWords = () => {
    if (!validateInputsForPinyin()) return;

    const tmp_article = parse(formData.article || '');
    const articleWithBold = parseAndBold({
      article: tmp_article.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });

    const pinyinArticle = parse(formData.article_pinyin || '');
    const pinyinArticleWithBold = parseAndBold({
      article: pinyinArticle.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });

    setFormData((prev) => ({
      ...prev,
      article: articleWithBold,
      article_pinyin: pinyinArticleWithBold,
    }));
  };

  const validateInputsForPinyin = (): boolean => {
    if (!formData.article) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please enter an article first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!formData.grade) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please select a grade first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!formData.chapter) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please select a chapter first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<HomeworkFormData> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.grade) {
      newErrors.grade = 'Grade is required';
    }

    if (!formData.chapter) {
      newErrors.chapter = 'Chapter is required';
    }

    if (!formData.article.trim()) {
      newErrors.article = 'Article content is required';
    }

    if (!formData.question.trim()) {
      newErrors.question = 'Questions are required';
    }

    if (!formData.article_pinyin.trim()) {
      newErrors.article_pinyin =
        'Article Pinyin is required. Please generate it using the button.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(10);

    try {
      // Handle file upload if present
      if (formData.file && !isEmpty(formData.file)) {
        setUploadProgress(30);
        const { error: uploadError } = await uploadFile({
          file: formData.file,
          bucket: FILE_DB_BUCKET_NAME,
          supabase: userOps.supabaseClient,
        });

        if (uploadError) {
          toast({
            title: 'File Upload Failed',
            description: uploadError.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      setUploadProgress(70);

      // Prepare content for database
      const contentData: Omit<ArticleProfile, 'id' | 'title_article'> = {
        title: formData.title,
        grade: formData.grade,
        chapter: formData.chapter,
        article: formData.article,
        article_pinyin: formData.article_pinyin,
        question: formData.question,
        question_pinyin: formData.question_pinyin,
        author: full_name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        file_name: formData.file?.name || '',
        // Add other required fields with defaults
        deleted: false,
        image: '', // Default empty image
        word_count: countArticleCharacters(formData.article),
      };

      // Insert content to library
      const { error: contentError } = await userOps.insertData(
        TABLE_NAMES.LIBRARY,
        contentData,
      );

      if (contentError) {
        toast({
          title: 'Content Creation Failed',
          description: contentError.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setUploadProgress(90);

      // Notify teachers via email
      const { data: teacherData } = await userOps.getUsersByRole(
        UserRole.TEACHER,
      );

      if (teacherData && teacherData.length > 0) {
        console.log(
          `Notifying ${teacherData.length} teachers about new content`,
        );

        // Prepare teacher email info for the edge function
        const teacherEmailsInfo = teacherData.map((teacher: any) => ({
          email_address: {
            address: teacher.email,
            name: `${teacher.first_name} ${teacher.last_name}`,
          },
        }));

        // Send notification emails
        const { data: emailData, error: emailError } =
          await userOps.supabaseClient.functions.invoke('add-article-email', {
            body: {
              teacherEmailsInfo,
              teacherName: full_name,
              articleTitle: formData.title,
              grade: formData.grade,
            },
          });

        if (emailError) {
          console.error('Failed to send email notifications:', emailError);
          // Don't block the flow if email fails, just log it
          toast({
            title: 'Email notification warning',
            description:
              'Content created successfully, but email notifications could not be sent to all teachers.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
        } else {
          console.log('Email notifications sent successfully:', emailData);
        }
      }

      setUploadProgress(100);

      toast({
        title: 'Content Created Successfully',
        description:
          'New homework content has been added to the library and teachers have been notified.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Navigate back to homework list or library
      navigate('/admin/library');
    } catch (error) {
      console.error('Error creating homework:', error);
      toast({
        title: 'Unexpected Error',
        description:
          'An unexpected error occurred while creating the homework.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    navigate('/admin/library');
  };

  return (
    <FormPageLayout
      title="New Homework"
      breadcrumb={{
        items: [
          { label: 'Admin', href: '/admin' },
          { label: 'Library', href: '/admin/library' },
          { label: 'Create', isCurrent: true },
        ],
      }}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      submitText="Create Homework"
      maxWidth="1400px"
      loading={isSubmitting}
      submitDisabled={isSubmitting}
    >
      <VStack spacing={4} align="stretch">
        <Card padding={4} variant="outline">
          <VStack spacing={3} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
              <Box gridColumn={{ md: 'span 2' }}>
                <FormField label="Title" required error={errors.title}>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter homework title"
                    leftIcon={<FiBook />}
                  />
                </FormField>
              </Box>
              <FormField label="Grade" required error={errors.grade}>
                <Select
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  placeholder="Select grade"
                >
                  {ALL_GRADES.map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Chapter" required error={errors.chapter}>
                <Select
                  value={formData.chapter}
                  onChange={(e) => handleInputChange('chapter', e.target.value)}
                  placeholder="Select chapter"
                >
                  {ALL_CHAPTER.map((chapter) => (
                    <option key={chapter} value={chapter}>
                      {chapter}
                    </option>
                  ))}
                </Select>
              </FormField>
            </SimpleGrid>
          </VStack>
        </Card>

        <Card padding={4} variant="outline">
          <Flex
            gap={3}
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            mb={2}
          >
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                leftIcon={<RiPinyinInput />}
                colorScheme="red"
                onClick={handleGeneratePinyin}
              >
                Generate Pinyin
              </Button>
              <Button leftIcon={<RiBold />} onClick={handleBoldWords}>
                Bold New Words
              </Button>
            </ButtonGroup>
          </Flex>
          <Tabs
            index={articleTabIndex}
            onChange={setArticleTabIndex}
            colorScheme="blue"
            size="sm"
            isLazy
          >
            <TabList>
              <Tab>Article</Tab>
              <Tab>Pinyin</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pb={0} pt={3}>
                <FormField required error={errors.article}>
                  <ArticleEditor
                    value={formData.article}
                    onChange={(value) => handleInputChange('article', value)}
                    style={{ height: '320px' }}
                  />
                </FormField>
              </TabPanel>
              <TabPanel px={0} pb={0} pt={3}>
                <FormField required error={errors.article_pinyin}>
                  <ArticleEditor
                    value={formData.article_pinyin}
                    onChange={(value) =>
                      handleInputChange('article_pinyin', value)
                    }
                    style={{ height: '320px' }}
                  />
                </FormField>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>

        <Card padding={4} variant="outline">
          <Flex
            gap={3}
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            mb={2}
          >
            <Box>
              <Heading size="sm">Questions</Heading>
            </Box>
            <Button
              leftIcon={<RiPinyinInput />}
              colorScheme="red"
              variant="outline"
              onClick={handleGeneratePinyinForQuestion}
              size="sm"
            >
              Generate Pinyin
            </Button>
          </Flex>
          <Tabs
            index={questionTabIndex}
            onChange={setQuestionTabIndex}
            colorScheme="blue"
            size="sm"
            isLazy
          >
            <TabList>
              <Tab>Questions</Tab>
              <Tab>Pinyin</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pb={0} pt={3}>
                <FormField required error={errors.question}>
                  <QuestionEditor
                    value={formData.question}
                    onChange={(value) => handleInputChange('question', value)}
                  />
                </FormField>
              </TabPanel>
              <TabPanel px={0} pb={0} pt={3}>
                <QuestionEditor
                  value={formData.question_pinyin}
                  onChange={(value) =>
                    handleInputChange('question_pinyin', value)
                  }
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>

        <Accordion allowToggle>
          <AccordionItem border="1px solid" borderColor="gray.200" rounded="lg">
            <AccordionButton px={4} py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Icon as={FiUpload} />
                <Box>
                  <Text fontSize="sm" fontWeight="semibold">
                    Supporting material
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Optional PDF, document, audio, or image
                  </Text>
                </Box>
              </HStack>
              {formData.file && (
                <Text fontSize="xs" color="blue.500" mr={3} noOfLines={1}>
                  {formData.file.name}
                </Text>
              )}
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={4} pt={1} pb={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <input
                  type="file"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px dashed #CBD5E0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                  accept=".pdf,.doc,.docx,.mp3,.wav,.jpg,.png,.gif"
                />
                {formData.file && (
                  <Box
                    px={3}
                    py={2}
                    bg={selectedFileBg}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={selectedFileBorder}
                  >
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                      {formData.file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* Upload Progress */}
        {isSubmitting && uploadProgress > 0 && (
          <Card padding={3} variant="filled" background={subtleBg}>
            <Text fontSize="sm" fontWeight="600" mb={2}>
              Creating homework… {uploadProgress}%
            </Text>
            <Progress
              value={uploadProgress}
              size="sm"
              colorScheme="blue"
              rounded="full"
            />
          </Card>
        )}
      </VStack>
    </FormPageLayout>
  );
};
